import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import RefreshIcon from '@mui/icons-material/Refresh';
import api, { formatApiError } from '../api';
import { hasAssortment, matchSection, sectionsForVendorType } from '../assortments';
import { useAuth } from '../auth';
import { vendorCurrency } from '../constants';

const emptyForm = () => ({
  name: '',
  description: '',
  category: '',
  price_aed: '',
  price_display: '',
  discount_percent: '',
  discount_label: '',
  image_url: '',
  whatsapp: '',
  external_url: '',
  is_active: true,
  sort_order: 0,
});

export default function ProductsPage() {
  const { vendor } = useAuth();
  const currency = vendorCurrency(vendor);
  const assortmentSections = sectionsForVendorType(vendor?.vendor_type);
  const showAssortment = hasAssortment(vendor?.vendor_type);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Master Menu browse / copy (CMM sub-catalogs)
  const [mmOpen, setMmOpen] = useState(false);
  const [mmLoading, setMmLoading] = useState(false);
  const [mmCatalogs, setMmCatalogs] = useState([]);
  const [mmCatalogKind, setMmCatalogKind] = useState('grocery');
  const [mmProducts, setMmProducts] = useState([]);
  const [mmCategories, setMmCategories] = useState([]);
  const [mmTotal, setMmTotal] = useState(0);
  const [mmPage, setMmPage] = useState(1);
  const [mmQ, setMmQ] = useState('');
  const [mmQDraft, setMmQDraft] = useState('');
  const [mmCategory, setMmCategory] = useState('');
  const [mmCopy, setMmCopy] = useState(null);
  const [mmCopyForm, setMmCopyForm] = useState({
    price_aed: '',
    name: '',
    description: '',
    image_url: '',
    category: '',
  });
  const [mmCopyBusy, setMmCopyBusy] = useState(false);
  const [mmCopyPendingImage, setMmCopyPendingImage] = useState(null);
  const mmPageSize = 24;

  const formatPrice = (p) => {
    if (p.price_display) return p.price_display;
    if (p.price_aed != null) return `${currency} ${Number(p.price_aed).toFixed(2)}`;
    return '—';
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/vendor/products', {
        params: { include_inactive: true },
      });
      setProducts(res.data?.products || []);
    } catch (err) {
      setError(formatApiError(err, 'Failed to load products'));
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm({
      ...emptyForm(),
      whatsapp: vendor?.whatsapp_number || vendor?.phone_e164 || '',
    });
    setPendingImage(null);
    setImagePreview(null);
    setDialog('create');
  };

  const loadMasterMenu = useCallback(async () => {
    setMmLoading(true);
    try {
      const [listRes, catRes] = await Promise.all([
        api.get('/api/vendor/master-menu', {
          params: {
            q: mmQ || undefined,
            category: mmCategory || undefined,
            page: mmPage,
            page_size: mmPageSize,
            catalog_kind: mmCatalogKind,
          },
        }),
        api.get('/api/vendor/master-menu/categories', {
          params: { catalog_kind: mmCatalogKind },
        }),
      ]);
      setMmProducts(listRes.data?.products || []);
      setMmTotal(listRes.data?.total || 0);
      setMmCategories(catRes.data?.categories || []);
    } catch (err) {
      setError(formatApiError(err, 'Failed to load Master Menu'));
      setMmProducts([]);
    } finally {
      setMmLoading(false);
    }
  }, [mmQ, mmCategory, mmPage, mmCatalogKind]);

  const openMasterMenu = async () => {
    setMmOpen(true);
    setMmPage(1);
    setMmQ('');
    setMmQDraft('');
    setMmCategory('');
    setMmCopy(null);
    try {
      const res = await api.get('/api/vendor/master-menu/catalogs');
      const catalogs = res.data?.catalogs || [];
      const def = res.data?.default_catalog_kind || 'grocery';
      // LP only sees the Master Menu that matches their shop category.
      setMmCatalogKind(def);
      setMmCatalogs((catalogs || []).filter((c) => c.kind === def));
    } catch {
      const vt = (vendor?.vendor_type || '').toLowerCase();
      const kind =
        vt === 'pharmacy' ? 'pharmacy' : vt === 'laundry' || vt === 'cleaning' ? 'laundry' : 'grocery';
      setMmCatalogKind(kind);
      setMmCatalogs([
        kind === 'pharmacy'
          ? { kind: 'pharmacy', code: 'MMP', short_label: 'Pharmacy' }
          : kind === 'laundry'
            ? { kind: 'laundry', code: 'MML', short_label: 'Laundry' }
            : { kind: 'grocery', code: 'MMG', short_label: 'Grocery' },
      ]);
    }
  };

  useEffect(() => {
    if (!mmOpen) return;
    loadMasterMenu();
  }, [mmOpen, loadMasterMenu]);

  const openMmCopy = (p) => {
    setMmCopy(p);
    setMmCopyForm({
      // LP must set price — never prefill from Master Menu.
      price_aed: '',
      name: p.name || '',
      description: p.description || '',
      image_url: p.image_url || '',
      // Prefill assortment when Master Menu category maps to a shop section.
      category: showAssortment
        ? matchSection(vendor?.vendor_type, p.category) || ''
        : '',
    });
    setMmCopyPendingImage(null);
  };

  const confirmMmCopy = async () => {
    if (!mmCopy?.id) return;
    const priceRaw = mmCopyForm.price_aed;
    if (priceRaw === '' || priceRaw == null || Number.isNaN(Number(priceRaw))) {
      setError('Enter a selling price before copying');
      return;
    }
    setMmCopyBusy(true);
    setError('');
    try {
      const res = await api.post(`/api/vendor/master-menu/${mmCopy.id}/copy`, {
        price_aed: Number(priceRaw),
        name: mmCopyForm.name.trim() || undefined,
        description: mmCopyForm.description.trim() || undefined,
        image_url: mmCopyForm.image_url.trim() || undefined,
        // Empty string = explicit uncategorized (do not fall back to Master Menu category).
        ...(showAssortment ? { category: mmCopyForm.category.trim() } : {}),
      });
      const productId = res.data?.product?.id;
      if (mmCopyPendingImage && productId) {
        await uploadProductImage(productId, mmCopyPendingImage);
      }
      setSuccess(`Copied “${mmCopyForm.name || mmCopy.name}” to your menu`);
      setMmCopy(null);
      setMmOpen(false);
      await load();
    } catch (err) {
      setError(formatApiError(err, 'Copy failed'));
    } finally {
      setMmCopyBusy(false);
    }
  };

  const openEdit = (p) => {
    setForm({
      name: p.name || '',
      description: p.description || '',
      category: showAssortment
        ? matchSection(vendor?.vendor_type, p.category) || ''
        : '',
      price_aed: p.price_aed ?? '',
      price_display: p.price_display || '',
      discount_percent: p.discount_percent ?? '',
      discount_label: p.discount_label || '',
      image_url: p.image_url || '',
      whatsapp: p.whatsapp || '',
      external_url: p.external_url || '',
      is_active: p.is_active !== false,
      sort_order: p.sort_order ?? 0,
    });
    setPendingImage(null);
    setImagePreview(p.image_url || null);
    setDialog(p);
  };

  const onPickImage = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose a JPEG, PNG, WebP, or GIF image');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('Image must be 8MB or smaller');
      return;
    }
    setPendingImage(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target?.result || null);
    reader.readAsDataURL(file);
  };

  const uploadProductImage = async (productId, file) => {
    const body = new FormData();
    body.append('file', file);
    const res = await api.post(`/api/vendor/products/${productId}/upload-image`, body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data?.image_url || null;
  };

  const save = async () => {
    if (!form.name.trim()) {
      setError('Product name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const discRaw = form.discount_percent;
      let discountPercent = null;
      if (discRaw !== '' && discRaw != null) {
        discountPercent = Math.min(100, Math.max(0, Number(discRaw)));
        if (Number.isNaN(discountPercent)) discountPercent = null;
      }
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        category: showAssortment ? form.category.trim() || null : null,
        price_aed:
          form.price_aed === '' || form.price_aed == null ? null : Number(form.price_aed),
        price_display: form.price_display.trim() || null,
        discount_percent: discountPercent,
        discount_label: form.discount_label.trim() || null,
        image_url: form.image_url.trim() || null,
        whatsapp: form.whatsapp.trim() || null,
        external_url: form.external_url.trim() || null,
        is_active: !!form.is_active,
        sort_order: Number(form.sort_order) || 0,
      };
      let productId = dialog === 'create' ? null : dialog.id;
      if (dialog === 'create') {
        const res = await api.post('/api/vendor/products', payload);
        productId = res.data?.product?.id;
        setSuccess('Product added');
      } else {
        await api.put(`/api/vendor/products/${dialog.id}`, payload);
        setSuccess('Product updated');
      }
      if (pendingImage && productId) {
        const url = await uploadProductImage(productId, pendingImage);
        if (url) setSuccess((s) => `${s} · photo uploaded`);
      }
      setDialog(null);
      setPendingImage(null);
      setImagePreview(null);
      await load();
    } catch (err) {
      setError(formatApiError(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete “${p.name}”?`)) return;
    setError('');
    try {
      await api.delete(`/api/vendor/products/${p.id}`);
      setSuccess('Product deleted');
      await load();
    } catch (err) {
      setError(formatApiError(err, 'Delete failed'));
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={900}>
            Manage items
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Copy ready items from Master Menu (title + photo done — you set the price), or add
            manually.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading} size={isMobile ? 'small' : 'medium'}>
            Refresh
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<MenuBookIcon />}
            onClick={openMasterMenu}
            size={isMobile ? 'small' : 'medium'}
          >
            From Master Menu
          </Button>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate} size={isMobile ? 'small' : 'medium'}>
            Add manually
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : products.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No products yet. Add your first item (a Test {currency} 1 product may already exist from
          registration).
        </Alert>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {products.map((p) => (
            <Paper key={p.id} variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 1.5,
                    flexShrink: 0,
                    overflow: 'hidden',
                    bgcolor: '#f1f5f9',
                    border: '1px solid',
                    borderColor: 'divider',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  {p.image_url ? (
                    <Box
                      component="img"
                      src={p.image_url}
                      alt=""
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      —
                    </Typography>
                  )}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography fontWeight={800} noWrap>
                    {p.name}
                  </Typography>
                  {p.category && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {p.category}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mt: 0.5 }} fontWeight={700}>
                    {formatPrice(p)}
                    {p.discount_percent != null && Number(p.discount_percent) > 0
                      ? ` · ${Number(p.discount_percent)}% off`
                      : ''}
                  </Typography>
                  {p.discount_label ? (
                    <Typography variant="caption" color="primary" fontWeight={700}>
                      {p.discount_label}
                    </Typography>
                  ) : null}
                </Box>
                <Stack direction="row" alignItems="center" spacing={0.25}>
                  <Chip
                    size="small"
                    color={p.is_active ? 'success' : 'default'}
                    label={p.is_active ? 'Active' : 'Off'}
                    sx={{ fontWeight: 700 }}
                  />
                  <IconButton size="small" onClick={() => openEdit(p)} aria-label="Edit">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => remove(p)} aria-label="Delete">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'auto' }}>
          <Table size="small" sx={{ minWidth: 480 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 64 }}> </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right"> </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell sx={{ width: 64, py: 1 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        bgcolor: '#f1f5f9',
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'grid',
                        placeItems: 'center',
                      }}
                    >
                      {p.image_url ? (
                        <Box
                          component="img"
                          src={p.image_url}
                          alt=""
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={700}>{p.name}</Typography>
                    {p.category && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {p.category}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {formatPrice(p)}
                    {p.discount_percent != null && Number(p.discount_percent) > 0
                      ? ` · ${Number(p.discount_percent)}%`
                      : ''}
                    {p.discount_label ? (
                      <Typography variant="caption" color="primary" display="block" fontWeight={700}>
                        {p.discount_label}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={p.is_active ? 'success' : 'default'}
                      label={p.is_active ? 'Yes' : 'No'}
                      sx={{ fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(p)} aria-label="Edit">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => remove(p)}
                      aria-label="Delete"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog
        open={!!dialog}
        onClose={() => !saving && setDialog(null)}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
      >
        <DialogTitle>{dialog === 'create' ? 'Add product' : 'Edit product'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name *"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            {showAssortment ? (
              <FormControl fullWidth>
                <InputLabel id="lp-assortment-label">Section (optional)</InputLabel>
                <Select
                  labelId="lp-assortment-label"
                  label="Section (optional)"
                  value={form.category || ''}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, category: e.target.value }))
                  }
                >
                  <MenuItem value="">
                    <em>None — uncategorized</em>
                  </MenuItem>
                  {assortmentSections.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Optional — residents can filter by section in Neighbour (e.g. Fresh
                  Vegetables).
                </FormHelperText>
              </FormControl>
            ) : null}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                label={`Price (${currency})`}
                type="number"
                value={form.price_aed}
                onChange={(e) => setForm((f) => ({ ...f, price_aed: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Price display"
                value={form.price_display}
                onChange={(e) => setForm((f) => ({ ...f, price_display: e.target.value }))}
                fullWidth
                placeholder={`from ${currency} 99`}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                label="Discount %"
                type="number"
                value={form.discount_percent}
                onChange={(e) => setForm((f) => ({ ...f, discount_percent: e.target.value }))}
                fullWidth
                inputProps={{ min: 0, max: 100, step: 1 }}
                helperText="0–100"
              />
              <TextField
                label="Discount label"
                value={form.discount_label}
                onChange={(e) => setForm((f) => ({ ...f, discount_label: e.target.value }))}
                fullWidth
                placeholder="e.g. Weekend deal"
              />
            </Stack>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Product photo
              </Typography>
              {imagePreview ? (
                <Box
                  component="img"
                  src={imagePreview}
                  alt="Product preview"
                  sx={{
                    width: '100%',
                    maxHeight: 180,
                    objectFit: 'cover',
                    borderRadius: 2,
                    mb: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Upload a photo to Cloudflare storage (JPEG, PNG, WebP, GIF · max 8MB).
                </Typography>
              )}
              <Button variant="outlined" component="label" disabled={saving}>
                {pendingImage || imagePreview ? 'Change photo' : 'Upload photo'}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  hidden
                  onChange={(e) => {
                    onPickImage(e.target.files?.[0] || null);
                    e.target.value = '';
                  }}
                />
              </Button>
            </Box>
            <TextField
              label="WhatsApp for this product"
              value={form.whatsapp}
              onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.value }))}
              fullWidth
              helperText="Usually your shop WhatsApp"
            />
            <TextField
              label="External URL"
              value={form.external_url}
              onChange={(e) => setForm((f) => ({ ...f, external_url: e.target.value }))}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.is_active}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={mmOpen}
        onClose={() => !mmCopyBusy && setMmOpen(false)}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
      >
        <DialogTitle>From Master Menu</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Search ready products for your shop type, tap Copy, set your price, confirm.
          </Typography>
          {(() => {
            const active =
              (mmCatalogs.length ? mmCatalogs : []).find((c) => c.kind === mmCatalogKind) || {
                kind: mmCatalogKind,
                code:
                  mmCatalogKind === 'pharmacy'
                    ? 'MMP'
                    : mmCatalogKind === 'laundry'
                      ? 'MML'
                      : 'MMG',
                short_label:
                  mmCatalogKind === 'pharmacy'
                    ? 'Pharmacy'
                    : mmCatalogKind === 'laundry'
                      ? 'Laundry'
                      : 'Grocery',
              };
            return (
              <Chip
                label={`${active.code || ''} ${active.short_label || active.label || active.kind}`.trim()}
                size="small"
                color="primary"
                sx={{ fontWeight: 700, mb: 1.5 }}
              />
            );
          })()}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
            <TextField
              size="small"
              label="Search"
              value={mmQDraft}
              onChange={(e) => setMmQDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setMmPage(1);
                  setMmQ(mmQDraft.trim());
                }
              }}
              fullWidth
            />
            <Button
              variant="outlined"
              onClick={() => {
                setMmPage(1);
                setMmQ(mmQDraft.trim());
              }}
            >
              Search
            </Button>
          </Stack>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            <Chip
              label="All"
              size="small"
              color={!mmCategory ? 'primary' : 'default'}
              onClick={() => {
                setMmPage(1);
                setMmCategory('');
              }}
              sx={{ fontWeight: 700 }}
            />
            {mmCategories.slice(0, 16).map((c) => (
              <Chip
                key={c.category}
                label={`${c.category} (${c.count})`}
                size="small"
                color={mmCategory === c.category ? 'primary' : 'default'}
                onClick={() => {
                  setMmPage(1);
                  setMmCategory(c.category);
                }}
                sx={{ fontWeight: 700 }}
              />
            ))}
          </Stack>
          {mmLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : mmProducts.length === 0 ? (
            <Alert severity="info">
              No items in this catalog yet. Ask Super Admin to seed Grocery, Pharmacy, or Laundry.
            </Alert>
          ) : (
            <Stack spacing={1}>
              {mmProducts.map((p) => (
                <Paper key={p.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        bgcolor: '#f1f5f9',
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                      }}
                    >
                      {p.image_url ? (
                        <Box
                          component="img"
                          src={p.image_url}
                          alt=""
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                          sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        '—'
                      )}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography fontWeight={800} noWrap>
                        {p.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {[p.category, p.unit_size].filter(Boolean).join(' · ')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        You set the price when copying
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => openMmCopy(p)}
                    >
                      Copy
                    </Button>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
          {mmTotal > mmPageSize ? (
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
              <Button disabled={mmPage <= 1} onClick={() => setMmPage((p) => p - 1)}>
                Previous
              </Button>
              <Typography sx={{ alignSelf: 'center' }} variant="body2">
                Page {mmPage} / {Math.max(1, Math.ceil(mmTotal / mmPageSize))}
              </Typography>
              <Button
                disabled={mmPage >= Math.ceil(mmTotal / mmPageSize)}
                onClick={() => setMmPage((p) => p + 1)}
              >
                Next
              </Button>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMmOpen(false)} disabled={mmCopyBusy}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!mmCopy}
        onClose={() => !mmCopyBusy && setMmCopy(null)}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
      >
        <DialogTitle>Copy to your menu</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {mmCopyForm.image_url ? (
              <Box
                component="img"
                src={mmCopyForm.image_url}
                alt=""
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
                sx={{
                  width: 96,
                  height: 96,
                  objectFit: 'contain',
                  borderRadius: 1.5,
                  bgcolor: '#f1f5f9',
                }}
              />
            ) : null}
            <TextField
              label="Name"
              value={mmCopyForm.name}
              onChange={(e) => setMmCopyForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Description"
              value={mmCopyForm.description}
              onChange={(e) => setMmCopyForm((f) => ({ ...f, description: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label={`Your price (${currency}) *`}
              type="number"
              value={mmCopyForm.price_aed}
              onChange={(e) => setMmCopyForm((f) => ({ ...f, price_aed: e.target.value }))}
              fullWidth
              required
              helperText="Suggested price is a hint only — set what you sell for"
            />
            {showAssortment ? (
              <FormControl fullWidth>
                <InputLabel id="mm-copy-assortment-label">Section (optional)</InputLabel>
                <Select
                  labelId="mm-copy-assortment-label"
                  label="Section (optional)"
                  value={mmCopyForm.category || ''}
                  onChange={(e) =>
                    setMmCopyForm((f) => ({ ...f, category: e.target.value }))
                  }
                >
                  <MenuItem value="">
                    <em>None — uncategorized</em>
                  </MenuItem>
                  {assortmentSections.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Optional — residents can filter by section in Neighbour (e.g. Fresh
                  Vegetables).
                </FormHelperText>
              </FormControl>
            ) : null}
            <Button variant="outlined" component="label" disabled={mmCopyBusy}>
              {mmCopyPendingImage ? 'Change photo' : 'Optional: replace photo'}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  if (!file) return;
                  if (!file.type.startsWith('image/')) {
                    setError('Please choose a JPEG, PNG, WebP, or GIF image');
                    return;
                  }
                  setMmCopyPendingImage(file);
                  e.target.value = '';
                }}
              />
            </Button>
            {mmCopyPendingImage ? (
              <Typography variant="caption" color="text.secondary">
                Will upload: {mmCopyPendingImage.name}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMmCopy(null)} disabled={mmCopyBusy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={confirmMmCopy} disabled={mmCopyBusy}>
            {mmCopyBusy ? <CircularProgress size={20} /> : 'Confirm copy'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
